import {
  FC,
  PropsWithChildren,
  useMemo,
  useRef,
  useState,
  useCallback,
  useContext,
  useEffect,
} from 'react';

import { Form, message } from 'antd';

import {
  GenericDataFormContext,
  QuotationInfoContext,
} from '../../../../shared/contexts';
import { StepTwoFormContext } from './StepTwoFormContext';

import QuotationService from '../../../../../../../../shared/services/quotation';

import {
  Products,
  SelectOption,
} from '../../../../../../../../shared/interfaces';
import {
  LOADING_SAVED_QUOTATIONS_STATES,
  PLAN_TYPE,
  QuotationStep,
} from '../../../../shared';
import { HeaderTabPersonalEffects } from '../components/TabsPropertyDataPersonalEffects/components';
import { TabsPropertyDataPersonalEffects } from '../components/TabsPropertyDataPersonalEffects';
import { LOADING_RENEWAL_QUOTATION_STATES } from '../../../../shared/interfaces/loading-renewal-quotation-states-enum';
import { LoadQuotationToRenewResponse } from '../../../../shared/interfaces/load-renewal-quotation-response-interface';

const planTypeEquivalent: { A: boolean; C: boolean } = {
  A: true,
  C: false,
};

export const StepTwoFormProvider: FC<PropsWithChildren> = ({ children }) => {
  const {
    quotationInfo,
    currentStep,
    loadingSavedQuotation,
    savedQuotation,
    loadSaveQuotationState,
    setLoadSaveQuotationState,
    setRenewalQuotationState,
    renewalQuotationState,
    quotationToRenewal
  } = useContext(QuotationInfoContext);
  const { setCanShowSpinner, setPlansSelected, getLocalitiesPerProvince } =
    useContext(GenericDataFormContext);

  const INITIAL_KEY_TAB = 1;
  // Cobertura de bien único: el backend del PDF solo toma getBien()
  const MAX_TABS = 1;
  const [activeKey, setActiveKey] = useState<number>(INITIAL_KEY_TAB);
  const [personalEffectsFormValues, setPersonalEffectsFormValues] =
    useState<any>();

  const [plansByActivityList, setPlansByActivityList] = useState<
    Array<Products>
  >([]);
  const [planOptionsBySelectedPlan, setPlanOptionsBySelectedPlan] = useState<
    Array<SelectOption>
  >([]);
  const [planTypeSelected, setPlanTypeSelected] = useState<'A' | 'C'>('A');
  const [isPlanTypeADisabled, setIsPlanTypeADisabled] = useState(false);
  const [isPlanTypeCDisabled, setIsPlanTypeCDisabled] = useState(false);

  const [areTabActionsAllowed, setAreTabActionsAllowed] =
    useState<boolean>(true);

  const [showModal, setShowModal] = useState<boolean>(false);
  const [keyTabToDelete, setKeyTabToDelete] = useState<number | null>(null);
  const [tabs, setTabs] = useState<Array<any>>([]);

  const [stepTwoForm] = Form.useForm<any>();

  const newTabIndex = useRef(INITIAL_KEY_TAB);

  const addNewTab = useCallback(() => {
    if (tabs.length < MAX_TABS) {
      const newActiveKey = ++newTabIndex.current;
      const isTabActionEnabled = planTypeEquivalent[planTypeSelected ?? 'C'];

      setTabs([
        ...tabs,
        {
          label: (
            <HeaderTabPersonalEffects
              id={newActiveKey}
              areTabActionsAllowed={isTabActionEnabled}
            />
          ),
          children: <TabsPropertyDataPersonalEffects id={newActiveKey} />,
          key: newActiveKey,
          closable: false,
          forceRender: true,
        },
      ]);
      setActiveKey(newActiveKey);

      return;
    }

    message.error('Solo puede agregar 1 Efecto Personal (cobertura de bien único)');
  }, [tabs, newTabIndex, setTabs, setActiveKey, planTypeSelected]);

  const duplicateTab = useCallback(
    (id: any) => {
      if (tabs.length < MAX_TABS) {
        const tabSelected = tabs.find((tab: any) => tab.key === id);
        const newActiveKey = ++newTabIndex.current;
        const isTabActionEnabled = planTypeEquivalent[planTypeSelected ?? 'C'];

        if (tabSelected) {
          const newTab = {
            ...tabSelected,
            key: newActiveKey,
            label: (
              <HeaderTabPersonalEffects
                id={newActiveKey}
                areTabActionsAllowed={isTabActionEnabled}
              />
            ),
            children: (
              <TabsPropertyDataPersonalEffects
                id={newActiveKey}
                duplicateId={id}
              />
            ),
            closable: false,
            forceRender: true,
          };
          setTabs([...tabs, newTab]);
          setActiveKey(newActiveKey);
        }

        return;
      }

      message.error('Solo puede agregar 1 Efecto Personal (cobertura de bien único)');
    },
    [tabs, newTabIndex, setTabs, setActiveKey, planTypeSelected]
  );

  const removeTab = useCallback(
    (targetKey: number) => {
      setCanShowSpinner(true);

      setTimeout(() => {
        const targetIndex = tabs.findIndex(
          (pane: any) => pane.key === targetKey
        );
        const newPanes = tabs.filter((pane: any) => pane.key !== targetKey);
        if (newPanes.length && targetKey === activeKey) {
          const { key } =
            newPanes[
              targetIndex === newPanes.length ? targetIndex - 1 : targetIndex
            ];
          setActiveKey(key);
        }
        setTabs(newPanes);
        setCanShowSpinner(false);
      }, 500);
    },
    [tabs, setTabs, setActiveKey, activeKey]
  );

  const resetTabs = useCallback(
    (unconfirmedPlanType: 'A' | 'C') => {
      const isTabActionEnabled = planTypeEquivalent[unconfirmedPlanType];
      setTabs([
        {
          label: (
            <HeaderTabPersonalEffects
              id={INITIAL_KEY_TAB}
              areTabActionsAllowed={isTabActionEnabled}
            />
          ),
          children: <TabsPropertyDataPersonalEffects id={INITIAL_KEY_TAB} />,
          key: INITIAL_KEY_TAB,
          closable: false,
          forceRender: true,
        },
      ]);

      stepTwoForm.resetFields();
      newTabIndex.current = INITIAL_KEY_TAB;
      setActiveKey(INITIAL_KEY_TAB);
    },
    [tabs, setTabs, setActiveKey]
  );

  const getListPlansActivity = useCallback(
    async (article: string, branch: string) => {
      setCanShowSpinner(true);
      try {
        const response = await QuotationService.getProductsListPerProducer(
          article,
          branch
        );

        if (response?.data?.productosPorProductor?.length) {
          const productosPorProductor = response?.data?.productosPorProductor;

          setPlansByActivityList(productosPorProductor);

          const hasPlanTypeA = productosPorProductor.some(
            (plan) => plan.tipoDePlan === PLAN_TYPE.OPEN
          );

          setPlanTypeSelected(hasPlanTypeA ? PLAN_TYPE.OPEN : PLAN_TYPE.CLOSED);
        }
      } catch (error) {
      } finally {
        setCanShowSpinner(false);
      }
    },
    [setCanShowSpinner]
  );

  useEffect(() => {
    if (plansByActivityList.length) {
      const hasPlanTypeA = plansByActivityList.some(
        (plan) => plan.tipoDePlan === PLAN_TYPE.OPEN
      );
      const hasPlanTypeC = plansByActivityList.some(
        (plan) => plan.tipoDePlan === PLAN_TYPE.CLOSED
      );

      setIsPlanTypeADisabled(!hasPlanTypeA);
      setIsPlanTypeCDisabled(!hasPlanTypeC);
    }
  }, [plansByActivityList]);

  const filterPlansByPlanTypeSelected = useCallback(() => {
    if (planTypeSelected) {
      const selectedPlanTypeValue: 'A' | 'C' = planTypeSelected;

      if (!personalEffectsFormValues) {
        setTabs([
          {
            label: (
              <HeaderTabPersonalEffects
                id={INITIAL_KEY_TAB}
                areTabActionsAllowed={planTypeEquivalent[selectedPlanTypeValue]}
              />
            ),
            children: <TabsPropertyDataPersonalEffects id={INITIAL_KEY_TAB} />,
            key: INITIAL_KEY_TAB,
            closable: false,
            forceRender: true,
          },
        ]);
      }

      setAreTabActionsAllowed(planTypeEquivalent[selectedPlanTypeValue]);

      const filteredPlans = plansByActivityList.filter(
        (plan) => plan.tipoDePlan === selectedPlanTypeValue
      );

      const plansOptions = filteredPlans.map((el) => ({
        label: `${el.codigo} - ${el.descripcionLarga}`,
        value: el.codigo,
      }));

      setPlanOptionsBySelectedPlan(plansOptions);
    }
  }, [plansByActivityList, planTypeSelected]);

  useEffect(() => {
    if (plansByActivityList.length && planTypeSelected) {
      filterPlansByPlanTypeSelected();
    }
  }, [
    filterPlansByPlanTypeSelected,
    planTypeSelected,
    plansByActivityList.length,
  ]);

  useEffect(() => {
    if (
      quotationInfo &&
      currentStep === QuotationStep.TWO &&
      !loadingSavedQuotation
    ) {
      getListPlansActivity(quotationInfo.article, quotationInfo.branch);
    }
  }, [quotationInfo, getListPlansActivity, currentStep, loadingSavedQuotation]);

  const fillStepTwoServices = useCallback(async (isRenewal: boolean) => {
    let quotationToProccess;
    if(isRenewal){
      quotationToProccess = quotationToRenewal;
    }else{
      quotationToProccess = savedQuotation;
    }

    quotationToProccess.bienes.forEach(async (bien: any) => {
      await getLocalitiesPerProvince(bien.provincia);
    });

    await getListPlansActivity(quotationInfo!.article, quotationInfo!.branch);
    if (isRenewal) {
      setRenewalQuotationState(LOADING_RENEWAL_QUOTATION_STATES.STEP_TWO_SERVICES_LOADED_RENEWAL)
    }else{
      setLoadSaveQuotationState(
        LOADING_SAVED_QUOTATIONS_STATES.STEP_TWO_SERVICES_LOADED
      );
    }
  }, [
    getListPlansActivity,
    getLocalitiesPerProvince,
    savedQuotation,
    quotationToRenewal,
    setLoadSaveQuotationState,
    quotationInfo,
  ]);

  const fillStepTwoData = useCallback(async (isRenewal: boolean) => {
    const _tabs: any = [];
    const _inputs: any = [];
    const plansSelected: string[] = [];
    let quotationToProccess: LoadQuotationToRenewResponse;
    if(isRenewal){
      quotationToProccess = quotationToRenewal;
    }else{
      quotationToProccess = savedQuotation;
    }
    quotationToProccess.bienes.forEach((bien: any, index: number) => {
      const selectedPlanTypeValue: 'A' | 'C' = quotationToProccess.tipoDePlan!;
      const _activeKey = index + 1; 
      _tabs.push({
        label: (
          <HeaderTabPersonalEffects
            id={_activeKey}
            areTabActionsAllowed={planTypeEquivalent[selectedPlanTypeValue]}
          />
        ),
        children: <TabsPropertyDataPersonalEffects id={_activeKey} />,
        key: _activeKey,
        closable: index !== 0,
        forceRender: true,
      });

      if (
        bien.planSeleccionado &&
        !plansSelected.includes(bien.planSeleccionado)
      ) {
        plansSelected.push(String(bien.planSeleccionado));
      }

      _inputs.push({
        name: `plan-tab-${_activeKey}`,
        value: `${bien.planSeleccionado}`,
      });
      _inputs.push({
        name: `province-tab-${_activeKey}`,
        value: `${bien.provincia}`,
      });
      _inputs.push({
        name: `location-tab-${_activeKey}`,
        value: `${bien.sufijoPostal}${bien.codigoPostal}`,
      });
      _inputs.push({
        name: `propertyDataAddress${_activeKey}`,
        value: `${bien.domicilio}`,
      });
    });

    setPlansSelected(plansSelected);

    setTabs(_tabs);
    setActiveKey(INITIAL_KEY_TAB);
    stepTwoForm.setFields(_inputs);
    setPersonalEffectsFormValues(_inputs);
  }, [
    savedQuotation,
    quotationToRenewal,
    setActiveKey,
    setTabs,
    stepTwoForm,
    setPersonalEffectsFormValues,
    setPlansSelected,
  ]);

  useEffect(() => {
    if (
      loadSaveQuotationState ===
      LOADING_SAVED_QUOTATIONS_STATES.STEP_ONE_DATA_FILLED
    ) {
      setLoadSaveQuotationState(
        LOADING_SAVED_QUOTATIONS_STATES.STEP_TWO_SERVICES_LOADING
      );
      fillStepTwoServices(false);
    }
  }, [loadSaveQuotationState]);

  useEffect(() => {
    if (
      loadSaveQuotationState ===
      LOADING_SAVED_QUOTATIONS_STATES.STEP_TWO_SERVICES_LOADED
    ) {
      setLoadSaveQuotationState(
        LOADING_SAVED_QUOTATIONS_STATES.STEP_TWO_DATA_FILLING
      );
      fillStepTwoData(false);
    }
  }, [loadSaveQuotationState]);

  useEffect(() => {    
    if (
      loadSaveQuotationState ===
        LOADING_SAVED_QUOTATIONS_STATES.STEP_TWO_DATA_FILLING &&
      tabs.length === savedQuotation.bienes.length
    ) {
      setLoadSaveQuotationState(
        LOADING_SAVED_QUOTATIONS_STATES.STEP_TWO_DATA_FILLED
      );
    }
  }, [loadSaveQuotationState, savedQuotation, tabs]);

  /**
   * Renewal
   */

  useEffect(() => {
    if (
      renewalQuotationState ===
      LOADING_RENEWAL_QUOTATION_STATES.STEP_ONE_DATA_FILLED_RENEWAL
    ) {
      setRenewalQuotationState(
        LOADING_RENEWAL_QUOTATION_STATES.STEP_TWO_SERVICES_LOADING_RENEWAL
      );
      fillStepTwoServices(true);
    }
  }, [renewalQuotationState]);

  useEffect(() => {
    if (
      renewalQuotationState ===
      LOADING_RENEWAL_QUOTATION_STATES.STEP_TWO_SERVICES_LOADED_RENEWAL
    ) {
      setRenewalQuotationState(
        LOADING_RENEWAL_QUOTATION_STATES.STEP_TWO_DATA_FILLING_RENEWAL
      );
      fillStepTwoData(true);
    }
  }, [renewalQuotationState]);

  useEffect(() => {
    if (
      renewalQuotationState ===
        LOADING_RENEWAL_QUOTATION_STATES.STEP_TWO_DATA_FILLING_RENEWAL &&
      tabs.length === quotationToRenewal.bienes.length
    ) {
      setRenewalQuotationState(
        LOADING_RENEWAL_QUOTATION_STATES.STEP_TWO_DATA_FILLED_RENEWAL
      );
    }
  }, [renewalQuotationState, quotationToRenewal, tabs]);

  const values = useMemo(
    () => ({
      stepTwoForm,
      activeKey,
      setActiveKey,
      tabs,
      setTabs,
      duplicateTab,
      removeTab,
      addNewTab,
      keyTabToDelete,
      setKeyTabToDelete,
      showModal,
      setShowModal,
      planOptionsBySelectedPlan,
      filterPlansByPlanTypeSelected,
      areTabActionsAllowed,
      planTypeSelected,
      setPlanTypeSelected,
      resetTabs,
      personalEffectsFormValues,
      setPersonalEffectsFormValues,
      isPlanTypeADisabled,
      isPlanTypeCDisabled,
    }),
    [
      stepTwoForm,
      activeKey,
      setActiveKey,
      tabs,
      setTabs,
      duplicateTab,
      removeTab,
      addNewTab,
      keyTabToDelete,
      setKeyTabToDelete,
      showModal,
      setShowModal,
      planOptionsBySelectedPlan,
      filterPlansByPlanTypeSelected,
      areTabActionsAllowed,
      planTypeSelected,
      setPlanTypeSelected,
      resetTabs,
      personalEffectsFormValues,
      setPersonalEffectsFormValues,
      isPlanTypeADisabled,
      isPlanTypeCDisabled,
    ]
  );

  return (
    <StepTwoFormContext.Provider value={values}>
      {children}
    </StepTwoFormContext.Provider>
  );
};
